"use client";

import { useEffect } from "react";
import { useUser } from "@clerk/nextjs";
import { getWebPushAvailability, isNativeCapacitorApp } from "@/lib/client/capacitor";
import {
  getOneSignalAppId,
  getOneSignalSafariWebId,
  initializeNativeOneSignal,
} from "@/lib/client/oneSignal";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "";
const ONE_SIGNAL_SCRIPT_SELECTOR =
  'script[data-kurb-onesignal], script[src$="/sdks/web/v16/OneSignalSDK.page.js"]';
const INTERACTION_EVENTS = ["click", "pointerdown", "keydown", "touchstart"] as const;

type NotificationClickEvent = {
  notification?: {
    launchURL?: string;
  };
};

type WebOneSignal = {
  init: (options: {
    appId: string;
    safari_web_id: string;
    notifyButton: { enable: false };
  }) => Promise<void> | void;
  Notifications: {
    addEventListener: (event: "click", listener: (event: NotificationClickEvent) => void) => void;
  };
};

type OneSignalDeferredCallback = (oneSignal: WebOneSignal) => Promise<void> | void;

type KurbWindow = {
  OneSignalDeferred?: OneSignalDeferredCallback[];
  __kurbOneSignalInitPromise?: Promise<void>;
  __kurbOneSignalLoadRequested?: boolean;
};

const getKurbWindow = () => window as unknown as KurbWindow;

const markNotificationAsRead = async (url: string) => {
  try {
    const response = await fetch(`${API_URL}/api/v2/notification-history?limit=50`, {
      credentials: "include",
    });
    const data = await response.json();
    const notifications = data?.data?.notifications;
    const notification = notifications?.find(
      (candidate: { url?: string; id?: string; is_read?: boolean }) =>
        candidate.url === url && !candidate.is_read
    );

    if (notification?.id) {
      await fetch(`${API_URL}/api/v2/notification-history/${notification.id}/read`, {
        method: "PUT",
        credentials: "include",
      });
    }
  } catch (error) {
    console.error("Error marking notification as read:", error);
  }
};

const initializeWebOneSignal: OneSignalDeferredCallback = async (oneSignal) => {
  const browserWindow = getKurbWindow();

  if (browserWindow.__kurbOneSignalInitPromise) {
    return browserWindow.__kurbOneSignalInitPromise;
  }

  browserWindow.__kurbOneSignalInitPromise = (async () => {
    await oneSignal.init({
      appId: getOneSignalAppId(),
      safari_web_id: getOneSignalSafariWebId(),
      notifyButton: { enable: false },
    });

    oneSignal.Notifications.addEventListener("click", (event) => {
      const url = event.notification?.launchURL;
      if (url) {
        void markNotificationAsRead(url);
      }
    });
  })();

  return browserWindow.__kurbOneSignalInitPromise;
};

const loadWebOneSignal = () => {
  const browserWindow = getKurbWindow();

  if (browserWindow.__kurbOneSignalLoadRequested) {
    return;
  }

  browserWindow.__kurbOneSignalLoadRequested = true;
  browserWindow.OneSignalDeferred = browserWindow.OneSignalDeferred || [];
  browserWindow.OneSignalDeferred.push(initializeWebOneSignal);

  if (document.querySelector(ONE_SIGNAL_SCRIPT_SELECTOR)) {
    return;
  }

  const script = document.createElement("script");
  script.src = "/sdks/web/v16/OneSignalSDK.page.js";
  script.defer = true;
  script.dataset.kurbOnesignal = "true";
  document.head.appendChild(script);
};

export const requestOneSignalLoad = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (isNativeCapacitorApp()) return;
  loadWebOneSignal();
};

type OneSignalScriptProps = {
  userId?: string | null;
};

const OneSignalScript = ({ userId = null }: OneSignalScriptProps) => {
  const { user, isLoaded } = useUser();
  const nativeUserId = userId ?? (isLoaded ? (user?.id ?? null) : undefined);

  useEffect(() => {
    if (isNativeCapacitorApp()) {
      void initializeNativeOneSignal(nativeUserId).catch((error) => {
        console.error("Native OneSignal initialization failed:", error);
      });
    }
  }, [nativeUserId]);

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      return;
    }

    if (getWebPushAvailability() !== "available") {
      return;
    }

    const eventOptions: AddEventListenerOptions = { passive: true, once: true };
    const handleInteraction = () => {
      INTERACTION_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleInteraction, eventOptions)
      );
      requestOneSignalLoad();
    };

    INTERACTION_EVENTS.forEach((event) =>
      window.addEventListener(event, handleInteraction, eventOptions)
    );

    return () => {
      INTERACTION_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleInteraction, eventOptions)
      );
    };
  }, []);

  return null;
};

export default OneSignalScript;
